/**
 * EnrichPanel — governed enrichment on a record: before/after diffs,
 * source + confidence badges, per-field Approve/Reject, tiered auto-apply
 * with a "revert from ledger" affordance.
 *
 * Thin by design: ALL side effects are injected. The caller feeds the
 * worker path in (`fetchWorkerFields`, typically argoWorker behind a
 * bridge) and owns every commit (`onApprove` / `onAutoApply` / `onRevert`
 * route into the existing approvals pipeline + sealed ledger). This
 * component never touches the network, a provider, or the store — the
 * whole decision engine is the pure pipeline in
 * src/lib/crm/enrichPipeline.ts, so the governance shown here is exactly
 * the governance tested there.
 */

import { useState } from 'react';
import {
  BadgeCheck,
  Check,
  Loader2,
  ShieldAlert,
  Sparkles,
  Undo2,
  X,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  autoApplyDecision,
  buildProposal,
  classOfField,
  summarizeEnrichRun,
  toActionDiff,
  DEFAULT_ENRICH_POLICY,
  type EnrichActionDiff,
  type EnrichField,
  type EnrichPolicy,
  type EnrichProposal,
  type EnrichRunSummary,
} from '../lib/crm/enrichPipeline';

export interface EnrichPanelProps {
  /** The record being enriched (account or person shape — plain data). */
  record: Record<string, any>;
  /**
   * Caller-provided worker path (argoWorker bridge). Resolve to the
   * worker's proposed fields, or null when unconfigured/failed — the panel
   * then falls back to the deterministic no-hallucination pipeline.
   */
  fetchWorkerFields?: () => Promise<EnrichField[] | null>;
  /**
   * Whether the AI agent worker is actually connected. Drives the honest
   * "connect AI to research the company" copy — when false, the panel says
   * up front that Enrich will only normalize data already on the record.
   */
  workerConfigured?: boolean;
  /** Tiered-autonomy policy per enrichment class. Defaults conservative. */
  policy?: EnrichPolicy;
  /** Operator approved one gated field — caller commits it (approvals seal). */
  onApprove: (field: EnrichField, diff: EnrichActionDiff) => void | Promise<void>;
  /** Fields the earned 'auto' tier applied — caller commits the batch. */
  onAutoApply?: (fields: EnrichField[], diff: EnrichActionDiff) => void | Promise<void>;
  /** Revert one auto-applied field from the sealed ledger (governed restore). */
  onRevert?: (field: EnrichField, diff: EnrichActionDiff) => void | Promise<void>;
  /** Viewer role etc. — disables the Enrich button and all commits. */
  disabled?: boolean;
}

type Phase = 'idle' | 'loading' | 'ready' | 'empty' | 'error';
type FieldState = 'pending' | 'approving' | 'approved' | 'rejected';

interface GatedRow {
  field: EnrichField;
  state: FieldState;
}

interface AutoRow {
  field: EnrichField;
  reverted: boolean;
  reverting: boolean;
}

const SOURCE_PILL: Record<EnrichField['source'], string> = {
  worker: 'glass-pill glass-pill--magenta',
  deterministic: 'glass-pill glass-pill--blue',
};

function pct(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function renderValue(v: any): string {
  if (v == null || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

export function EnrichPanel({
  record,
  fetchWorkerFields,
  workerConfigured = false,
  policy = DEFAULT_ENRICH_POLICY,
  onApprove,
  onAutoApply,
  onRevert,
  disabled = false,
}: EnrichPanelProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<EnrichProposal | null>(null);
  const [summary, setSummary] = useState<EnrichRunSummary | null>(null);
  const [gated, setGated] = useState<GatedRow[]>([]);
  const [auto, setAuto] = useState<AutoRow[]>([]);

  const run = async () => {
    setPhase('loading');
    setError(null);
    setSummary(null);
    setGated([]);
    setAuto([]);
    try {
      // Real worker path when the caller wired one; graceful deterministic
      // fallback (null / throw / empty) — never a fabricated result.
      let workerFields: EnrichField[] | null = null;
      if (fetchWorkerFields) {
        try {
          workerFields = await fetchWorkerFields();
        } catch {
          workerFields = null; // fall back deterministically, honestly labelled
        }
      }
      const next = buildProposal(record, workerFields);
      setProposal(next);
      if (next.fields.length === 0) {
        setSummary(summarizeEnrichRun(next, { autoApply: [], gate: [] }, workerConfigured));
        setPhase('empty');
        return;
      }
      const decision = autoApplyDecision(next, policy);
      setSummary(summarizeEnrichRun(next, decision, workerConfigured));
      setGated(decision.gate.map((field) => ({ field, state: 'pending' as const })));
      setAuto(
        decision.autoApply.map((field) => ({ field, reverted: false, reverting: false })),
      );
      if (decision.autoApply.length > 0 && onAutoApply) {
        await onAutoApply(
          decision.autoApply,
          toActionDiff({ ...next, fields: decision.autoApply }),
        );
      }
      setPhase('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrichment failed. Try again.');
      setPhase('error');
    }
  };

  const approve = async (row: GatedRow) => {
    if (!proposal) return;
    setGated((rows) =>
      rows.map((r) => (r.field === row.field ? { ...r, state: 'approving' } : r)),
    );
    try {
      await onApprove(row.field, toActionDiff({ ...proposal, fields: [row.field] }));
      setGated((rows) =>
        rows.map((r) => (r.field === row.field ? { ...r, state: 'approved' } : r)),
      );
    } catch (err) {
      setGated((rows) =>
        rows.map((r) => (r.field === row.field ? { ...r, state: 'pending' } : r)),
      );
      setError(err instanceof Error ? err.message : 'Could not commit the field.');
    }
  };

  const reject = (row: GatedRow) => {
    // A reject writes NOTHING — the field simply never reaches the record.
    setGated((rows) =>
      rows.map((r) => (r.field === row.field ? { ...r, state: 'rejected' } : r)),
    );
  };

  const revert = async (row: AutoRow) => {
    if (!proposal || !onRevert) return;
    setAuto((rows) =>
      rows.map((r) => (r.field === row.field ? { ...r, reverting: true } : r)),
    );
    try {
      await onRevert(row.field, toActionDiff({ ...proposal, fields: [row.field] }));
      setAuto((rows) =>
        rows.map((r) =>
          r.field === row.field ? { ...r, reverted: true, reverting: false } : r,
        ),
      );
    } catch (err) {
      setAuto((rows) =>
        rows.map((r) => (r.field === row.field ? { ...r, reverting: false } : r)),
      );
      setError(err instanceof Error ? err.message : 'Could not revert the field.');
    }
  };

  const badgeRow = (field: EnrichField) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className={SOURCE_PILL[field.source]}>{field.source}</span>
      <span className="glass-pill" title="Confidence">
        {pct(field.confidence)}
      </span>
      <span className="t-meta">{classOfField(field.field)}</span>
    </div>
  );

  const diffLine = (field: EnrichField) => (
    <div style={{ fontSize: 13, lineHeight: 1.5 }}>
      <strong>{field.field}</strong>
      {': '}
      <span style={{ color: C.textDim, textDecoration: 'line-through' }}>
        {renderValue(field.before)}
      </span>
      {' → '}
      <span>{renderValue(field.after)}</span>
    </div>
  );

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Sparkles size={16} color={C.magenta} aria-hidden />
          <span className="t-section-title">Governed enrichment</span>
          <span
            className={
              workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'
            }
            title={
              workerConfigured
                ? 'The AI agent worker is connected — Enrich researches the company.'
                : 'No AI worker connected — Enrich only normalizes data already on the record.'
            }
          >
            {workerConfigured ? 'AI connected' : 'AI not connected'}
          </span>
        </div>
        <button
          type="button"
          className="glass-btn glass-btn--primary"
          onClick={() => void run()}
          disabled={disabled || phase === 'loading'}
        >
          {phase === 'loading' ? (
            <>
              <Loader2
                size={14}
                aria-hidden
                style={{ animation: 'auth-spin 800ms linear infinite' }}
              />{' '}
              Enriching…
            </>
          ) : (
            'Enrich'
          )}
        </button>
      </div>

      {phase === 'idle' && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div className="t-meta">
            The agent researches this company and proposes field updates as
            before → after diffs. <strong>Nothing is written until you approve</strong> —
            low-risk normalizations may auto-apply under the earned tier, and every
            write is ledger-sealed and reversible.
          </div>
          {!workerConfigured && (
            <div className="t-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <ShieldAlert size={14} aria-hidden />
              Connect AI to enrich: without the agent worker, Enrich runs an honest
              deterministic pass that only normalizes data already on this record
              (domain, name, title, seniority) — it never invents company facts. Set
              the agent worker URL in Admin → Integrations for real company research.
            </div>
          )}
        </div>
      )}

      {phase === 'loading' && <div className="t-meta">Building the enrichment diff…</div>}

      {phase === 'empty' && (
        <div style={{ display: 'grid', gap: 4 }}>
          <div className="t-meta">
            {summary?.headline ??
              'No changes proposed — every derivable field is already normalized.'}
          </div>
          {summary && <div className="t-meta">{summary.sourceNote}</div>}
        </div>
      )}

      {(phase === 'error' || error) && (
        <div
          className="t-meta"
          role="alert"
          style={{ color: '#F87171', display: 'flex', gap: 6, alignItems: 'center' }}
        >
          <ShieldAlert size={14} aria-hidden /> {error ?? 'Enrichment failed. Try again.'}
        </div>
      )}

      {phase === 'ready' && proposal && (
        <>
          {summary && (
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ fontSize: 13 }}>
                <strong>{summary.headline}</strong>
              </div>
              <div className="t-meta">{summary.sourceNote}</div>
            </div>
          )}
          <div className="t-meta">
            Generated by <strong>{proposal.generatedBy}</strong> · risk{' '}
            <strong>{proposal.risk}</strong>
          </div>

          {auto.length > 0 && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="t-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <BadgeCheck size={14} color={C.green} aria-hidden />
                Auto-applied under the earned tier — sealed to the ledger, revert any time.
              </div>
              {auto.map((row) => (
                <div
                  key={row.field.field}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'grid', gap: 4 }}>
                    {diffLine(row.field)}
                    {badgeRow(row.field)}
                  </div>
                  {row.reverted ? (
                    <span className="glass-pill glass-pill--blue">reverted from ledger</span>
                  ) : onRevert ? (
                    <button
                      type="button"
                      className="glass-btn"
                      disabled={disabled || row.reverting}
                      onClick={() => void revert(row)}
                      title="Restore the sealed before-value through the governed pipeline"
                    >
                      <Undo2 size={13} aria-hidden />{' '}
                      {row.reverting ? 'Reverting…' : 'Revert from ledger'}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {gated.length > 0 && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="t-meta">
                Gated for your review — approve or reject each field. Rejects write nothing.
              </div>
              {gated.map((row) => (
                <div
                  key={row.field.field}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    opacity: row.state === 'rejected' ? 0.55 : 1,
                  }}
                >
                  <div style={{ display: 'grid', gap: 4 }}>
                    {diffLine(row.field)}
                    {badgeRow(row.field)}
                  </div>
                  {row.state === 'approved' ? (
                    <span className="glass-pill glass-pill--green">
                      <Check size={12} aria-hidden /> approved
                    </span>
                  ) : row.state === 'rejected' ? (
                    <span className="glass-pill">rejected</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="glass-btn glass-btn--primary"
                        disabled={disabled || row.state === 'approving'}
                        onClick={() => void approve(row)}
                      >
                        <Check size={13} aria-hidden />{' '}
                        {row.state === 'approving' ? 'Committing…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="glass-btn"
                        disabled={disabled}
                        onClick={() => reject(row)}
                      >
                        <X size={13} aria-hidden /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </GlassPanel>
  );
}
